import { useEffect } from "react";

import { Typography } from "@mui/material";

import { useWhiteScreen } from "../../../hooks/useWhiteScreen";
import { Container } from "../../common/Container/Container";

import "./terms_and_conditions.scss";

export function TermsAndConditions() {
	useWhiteScreen();

	useEffect(() => {
		const hash = location.hash;
		if (hash) {
			const id = hash.slice(1);
			document.getElementById(id)?.scrollIntoView();
		}
	}, []);

	return (
		<Container
			className="terms-and-conditions-container"
			navbarOrkaProps={{ white: true }}>
			<Typography className="normal-title" textAlign="center">
				TERMS OF USE AND SERVICE
			</Typography>

			<div className="section spacing-section">
				<div id="1-binding-agreement" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#1-binding-agreement">
					1. Binding Agreement
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					This Terms of Use and Service (the "Agreement") is a legal agreement
					between you and Fly ORKA, Inc. ("ORKA"). This Agreement governs your
					use of the ORKA website (the "ORKA Platform") and any purchases you
					make through the ORKA Platform.
				</Typography>

				<Typography className="normal-text spacing-content" textAlign="justify">
					<span style={{ paddingLeft: 32 }} />
					Please read this Agreement carefully because it is a legal contract
					and imposes obligations on with respect to your use of the ORKA
					Platform. Please note that you must be over the age of majority under
					the laws of your jurisdiction to enter into this agreement.
				</Typography>

				<Typography
					className="normal-text spacing-content"
					textAlign="justify"
					marginLeft="32px">
					BY CLICKING{" "}
					<Typography className="normal-text bold" component="b">
						"I ACCEPT"
					</Typography>
					, YOU WILL BE BOUND BY THIS AGREEMENT, AND YOU SIGNIFY YOUR CONSENT TO
					THE TERMS AND CONDITIONS OF THIS AGREEMENT.
				</Typography>

				<Typography
					className="normal-text spacing-content"
					textAlign="justify"
					marginLeft="32px">
					BY CLICKING{" "}
					<Typography className="normal-text bold" component="b">
						"I ACCEPT"
					</Typography>
					, YOU FURTHER AGREE THAT YOU HAVE REVIEWED AND ACCEPTED THE TERMS AND
					CONDITIONS OF THIS AGREEMENT BEFORE PURCHASING OR ATTENDING ANY
					INSTRUCTIONAL COURSES OR ACCESSING OR VIEWING ANY INSTRUCTIONAL
					CONTENT.
				</Typography>

				<Typography
					className="normal-text spacing-content"
					textAlign="justify"
					marginLeft="32px">
					IF YOU DO NOT AGREE TO THE TERMS AND CONDITIONS OF THIS AGREEMENT, YOU
					MAY NOT PURCHASE, ACCESS, VIEW, OR USE ANY INSTRUCTIONAL COURSES OR
					CONTENT.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="2-orka-platform" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#2-orka-platform">
					2. ORKA Platform
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					The ORKA Platform is a web-based communications platform which enables
					connections between Students and Instructors. "Students" are
					individuals seeking classes, courses, mentoring, coaching, support,
					and other instructional services related to aviation (collectively,
					"Instructional Courses"). "Instructors" are individuals seeking to
					provide Instruction Courses. If you agree to purchase Instructional
					Courses from an Instructor, you and such Instructor will enter into a
					Service Agreement directly between the two of you as described in{" "}
					<Typography
						className="normal-text hash-link"
						href="#3-service-agreement"
						component="a">
						Section 3
					</Typography>{" "}
					below. You must use the ORKA Platform to schedule Instructional
					Courses and make all payments.
				</Typography>

				<div className="square-box spacing-content">
					<Typography className="normal-text" textAlign="justify">
						The sole purpose of the ORKA Platform is to facilitate connections
						between Students and Instructors in order to enable Students to
						schedule and pay for Instructional Courses. ORKA is not responsible
						for the performance of Instructors or communications between ORKA
						Platform users, nor does ORKA have control over the quality, timing,
						legality, failure to provide, or any other aspect whatsoever of the
						Instructional Courses, nor of the integrity, responsibility,
						competence, qualifications, or any of the actions or omissions
						whatsoever of any Instructors.
					</Typography>

					<Typography
						className="normal-text spacing-content"
						textAlign="justify">
						INSTRUCTORS ARE INDEPENDENT CONTRACTORS OF STUDENTS AND NOT
						EMPLOYEES, PARTNERS, REPRESENTATIVES, AGENTS, JOINT VENTURERS,
						INDEPENDENT CONTRACTORS, OR FRANCHISEES OF ORKA. ORKA DOES NOT
						PROVIDE INSTRUCTIONAL COURSES AND DOES NOT EMPLOY INDIVIDUALS TO
						PROVIDE INSTRUCTIONAL COURSES. YOU HEREBY ACKNOWLEDGE THAT: (A) ORKA
						DOES NOT SUPERVISE, DIRECT, CONTROL, OR MONITOR INSTRUCTORS OR
						INSTRUCTIONAL COURSES; (B) ORKA MAKES NO WARRANTIES, EXPRESS OR
						IMPLIED, AND EXPRESSLY DISCLAIMS ALL RESPONSIBILITY AND LIABILITY
						FOR THE SUITABILITY, RELIABILITY, TIMELINESS, OR ACCURACY OF THE
						INSTRUCTIONAL COURSES OR ANY OTHER SERVICES SCHEDULED THROUGH THE
						ORKA PLATFORM, OR THEIR COMPLIANCE WITH ANY APPLICABLE LAW,
						REGULATION, OR CODE.
					</Typography>
				</div>
			</div>

			<div className="section spacing-section">
				<div id="3-service-agreement" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#3-service-agreement">
					3. Service Agreement
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					You acknowledge and agree that a legally binding contract (the
					"Service Agreement") is formed when you agree to purchase
					Instructional Courses from an Instructor. The terms of the Service
					Agreement include: (a) the terms and conditions set forth in this
					Section 3 and in the following Sections: 5-8, 14-15, and 18; (b) the
					relevant course description(s) provided on the ORKA Platform; (c) and
					any other contractual terms accepted by both the you and the
					Instructor (so long as such terms do not expand ORKA's obligations or
					restrict ORKA's rights under this Agreement).
				</Typography>

				<Typography className="normal-text spacing-content" textAlign="justify">
					You agree that ORKA is not a party to any Service Agreement and the
					formation of a Service Agreement will not, under any circumstance,
					create an employment or other service relationship between ORKA and
					the Instructor. ORKA's role is restricted solely to acting as a
					payment collection agent for the Instructor to facilitate payment on
					behalf of the Instructor through the ORKA Platform. In acting as the
					limited payment collection agent for particular Instructional Courses
					on the ORKA Platform, ORKA disclaims any other agency or authority to
					act on behalf of the Instructor, and assumes no liability or
					responsibility for any acts or omissions of the Instructor, either
					within or outside of the ORKA Platform. You acknowledge and agree that
					you are responsible for exercising your own independent business
					judgment in entering into a Service Agreement.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="4-course-descriptions" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#4-course-descriptions">
					4. Course Descriptions
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					The content and goals of Instructional Courses are described in the
					related course description. Descriptions are provided by Instructors,
					not ORKA. If you have any question about the content covered by an
					Instructional Course, you should contact ORKA before enrolling in the
					Instructional Course and ORKA will facilitate or obtain follow-up
					information from the Instructor.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="5-custom-lessons" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#5-custom-lessons">
					5. Custom Lessons
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Some Students require custom lessons to achieve the skill and
					knowledge levels of an Instructional Course. Your Instructor may
					recommend that you take a custom lesson at any time. Additional costs
					apply to custom lessons. If you decide to take custom lessons, you may
					schedule and pay for them through the ORKA Platform. For clarity,
					custom lessons are included within the definition of the term
					"Instructional Courses."
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="6-time-frame-for-course-completion"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#6-time-frame-for-course-completion">
					6. Time frame for Course Completion
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Each Instructional Course is designed to be completed by Students
					within the time frame designated in the relevant course description.
					If you are unable to perform the tasks and activities required by an
					Instructional Course during the designated time frame, the Instructor
					may (in the Instructor's discretion) address those deficiencies in
					through a custom class provided at an additional cost. Disengaging
					from an Instructional Course for more than 3 weeks will adversely
					affect your progress. If you disengage from an Instructional Course
					for more than 3 weeks you may be required to take custom lessons at an
					additional cost in order to successfully complete the relevant
					Instructional Course.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="7-changes-to-course-content-and-schedule"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#7-changes-to-course-content-and-schedule">
					7. Changes to Course Content and Schedule
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					From time to time, Instructors may change an Instructional Course by
					updating, modifying, adding, or removing classes or subject material
					to improve course effectiveness. Changes occurring after an
					Instructional Course begins will be noted in the applicable course
					description. An Instructor may reschedule any class if they believe
					safety could be compromised, if the equipment requires maintenance or
					repairs, or otherwise in an Instructor's reasonable discretion.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="8-support" className="section-scroll-position" />
				<Typography className="section-title" component="a" href="#8-support">
					8. Support
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Unless otherwise noted in the course description, each Instructional
					Course will include an appropriate level of mentoring, coaching, and
					other support from your Instructor as reasonably required by the
					course material (collectively, "Support"). Support will available to
					you during the time designated for the relevant Instructional Course.
					However, your Instructor may condition your continued access to
					Support on your self- discipline, diligence, punctuality, courtesy,
					and/or drive to improve. If an Instructor recommends for you to take a
					certain action more than once and you fail to take such action, you
					acknowledge and agree that you may not receive further Support. For
					clarity, Support does not include free instruction and is not a
					substitute for custom lessons if recommended by your Instructor. While
					Instructors will provide answers to specific technical questions left
					unanswered upon completing homework and reviewing study materials,
					Instructors do not have any obligation to answer questions stemming
					from a Student's failure to study.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="9-pricing-and-payments" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#9-pricing-and-payments">
					9. Pricing and Payments
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					You agree to pay all amounts due for Instructional Courses you
					purchase through the ORKA Platform. Unless otherwise provided in the
					applicable course description, payment for Instructional Courses must
					be made in full prior when signing up for the applicable Instructional
					Course. Pricing for Instructional Courses is not negotiable and ORKA
					reserves the right to restrict or suspend your access to Instructional
					Content until payments are received in full. All fees are exclusive of
					taxes and other charges imposed by government authorities and you are
					solely responsible for all sales, service, use, consumption and other
					taxes due on amounts payable under this agreement. Some components of
					pricing are outside of ORKA's control, including inflation, gas, oil,
					and airplane parts. ORKA may change pricing at anytime but such
					changes will not apply retroactively. ORKA encourages you to complete
					your Instructional Courses in the designated timeframe to avoid
					pricing volatility. Unless otherwise provided in the applicable course
					description, the following costs are not included in your payments for
					Instructional Content: (a) fees for FAA knowledge exam; and (b) fees
					related to instructional flights with a Designated Pilot Examiner or
					Check Airman.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="10-refund-and-cancellations"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#10-refund-and-cancellations">
					10. Refunds and Cancellations
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Orders for Instructional Courses are not cancellable and amounts paid
					through the ORKA Platform are not refundable for any reason, including
					your failure to attend a class, your failure to complete an
					Instructional Course during the designated time frame, and/or your
					overall satisfaction with any aspect of an Instructional Course. If
					you miss any portion of an Instructional Course, ORKA and/or the
					Instructor may allow you to reschedule in their sole discretion and
					subject to course availability.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="11-public-areas" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#11-public-areas">
					11. Public Areas
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					The ORKA Platform may provide certain functionality related to user
					profiles, reviews and ratings, chat areas, or other message or
					communication functions that allow you to communicate with Instructors
					and other users (collectively, "Public Areas"). You may not use the
					Public Areas: (a) to defame, abuse, harass, or violate the legal
					rights of others; (b) to post any content that you do not have the
					necessary rights or permissions to use; or (c) in a manner that
					violates any applicable law. You are solely responsible for content
					you post to Public Areas ("Your Content"). You acknowledge and agree
					that neither ORKA nor any Instructor is involved in the creation or
					development of Your Content and the ORKA Platform serves solely as a
					passive conduit for the distribution and publication of Your Content.
					You acknowledge that Your Content is not private and may be viewed
					others and that you may be identified by your name or login
					identification when communicating in Public Areas.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="12-license" className="section-scroll-position" />
				<Typography className="section-title" component="a" href="#12-license">
					12. License
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Subject to your payment and compliance with the terms of this
					Agreement, ORKA grants you the limited right and license to access and
					view content on the ORKA Platform and, only to the extent permitted in
					the applicable course description, view and/or download e-books,
					coursework, materials, instructional videos, or other educational
					materials provided to you in connection with Instructional Courses
					(collectively with the Instructional Courses, "Instructional
					Content"). You may not (a)reproduce or copy Instructional Content; (b)
					provide any other person with Instructional Content or with access to
					the Instructional Content (including your assigned login credentials);
					(c)make derivative works from the Instructional Content; (d)
					circumvent any security technology or software that is part of the
					ORKA platform or used to administer these use restrictions; or (e) use
					Instructional Content in any manner other than for your own personal,
					non-commercial use (if you are part of an organization and would like
					to broadcast ORKA's online classes, please contact ORKA directly).
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="13-ownership" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#13-ownership">
					13. Ownership
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					All text, graphics, data, designs, photographs, software, and other
					content and materials that users see or read through the ORKA
					Platform, other than Your Content, is and shall remain the sole and
					exclusive property of ORKA and its third-party licensors. No title to,
					ownership of, or other rights (other the limited license expressly
					provided herein) are conveyed or transferred to you by virtue of this
					Agreement. The trademarks, logos and service marks displayed on the
					ORKA Platform are the property of ORKA and you are not permitted to
					use these Marks without the prior written consent of ORKA. If you
					provide ORKA with any feedback related to the ORKA Platform or
					Instructional Content you acknowledge and agree that, by submitting
					such feedback, you hereby assign to ORKA all right, title, and
					interest in and to such feedback including all intellectual property
					rights therein.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="14-termination-and-monitoring"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#14-termination-and-monitoring">
					14. Termination and Monitoring
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					If ORKA has reason to believe that you have violated this Agreement,
					ORKA may suspend or terminate your use of the ORKA Platform and/or the
					Instructional Content. ORKA's right to suspend or terminate your use
					of ORKA Platform applies even if the violation was committed
					unintentionally or without your authorization if ORKA believes that
					suspension or termination is necessary to ensure compliance with
					applicable laws or to protect the rights, safety, privacy, security or
					property of ORKA or its third party licensors. ORKA may report any
					activity that it suspects violates any law or regulation to the
					appropriate authorities. Such reporting may include disclosing your
					data. ORKA reserves the right to terminate students who have failed to
					display the commitment, discipline, determination, and drive to learn
					required for the successful completion of Instructional Courses. ORKA
					may, but has no obligation to, monitor Public Areas and reserves the
					right to remove content from or disable access to Public Areas for any
					reason in ORKA's sole discretion and without notice.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="15-general-disclaimers-regarding-instructional-courses"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#15-general-disclaimers-regarding-instructional-courses">
					15. General Disclaimers Regarding Instructional Courses
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Please read the following disclaimers carefully as they provide
					further details regarding the scope of the Instructional Courses.
				</Typography>

				<div className="square-box spacing-content">
					<Typography className="normal-text" textAlign="justify">
						It is your responsibility to review the course description for all
						Instructional Courses prior to enrolling. ORKA will not provide a
						refund if you are dissatisfied with any Instructional Content or
						Instructor.
					</Typography>
				</div>

				<div className="square-box spacing-content">
					<Typography className="normal-text" textAlign="justify">
						Your success in an Instructional Course depends on your commitment,
						discipline, determination, and drive to learn. You are solely
						responsible for investing the time and effort to study and learn.
					</Typography>
				</div>

				<div className="square-box spacing-content">
					<Typography className="normal-text" textAlign="justify">
						The Instructional Courses are intended to build your general
						knowledge and skills. Neither ORKA nor any Instructor guarantees
						that Instructional Courses will enable you obtain a pilot license or
						any other certification or flying privileges. Obtain a pilot license
						or any other certification or flying privileges from the appropriate
						authority is solely your responsibility.
					</Typography>
				</div>
			</div>

			<div className="section spacing-section">
				<div id="16-warranty-disclaimer" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#16-warranty-disclaimer">
					16. Warranty Disclaimer
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					THE ORKA PLATFORM AND ALL CONTENT AND MATERIALS MADE AVAILABLE THROUGH
					THE ORKA PLATFORM ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
					WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY,
					OR OTHERWISE, INCLUDING ANY IMPLIED WARRANTY OF TITLE,
					MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-
					INFRINGEMENT, TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW. ORKA
					ASSUMES NO LIABILITY OR RESPONSIBILITY FOR ANY (A) ERRORS, MISTAKES,
					OR INACCURACIES OF CONTENT, DATA, OR INFORMATION POSTED, DISPLAYED,
					PUBLISHED, OR MADE AVAILABLE FOR DOWNLOAD OR USE ON THE ORKA PLATFORM,
					(B) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER,
					OCCURRING DURING AN INSTRUCTIONAL COURSE OR AS THE RESULT OF YOUR USE
					THE ORKA PLATFORM OR ANY INSTRUCTIONAL CONTENT, (C) ANY INTERRUPTION
					OR CESSATION OF TRANSMISSION TO OR FROM THE ORKA PLATFORM, OR (D) THE
					DEFAMATORY, OFFENSIVE OR ILLEGAL CONDUCT OF ANY THIRD PARTY NOT UNDER
					ORKA'S DIRECT CONTROL.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="17-limitation-of-liability"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#17-limitation-of-liability">
					17. Limitation of Liability
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW: (A) IN NO EVENT
					SHALL ORKA OR ITS THIRD PARTY LICENSORS BE LIABLE FOR ANY INDIRECT,
					PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES,
					INCLUDING WITHOUT LIMITATION DAMAGES FOR BUSINESS INTERRUPTION, LOSS
					OF PROFITS, GOODWILL, USE, DATA OR OTHER INTANGIBLE LOSSES ARISING OUT
					OF OR RELATING TO THE ORKA PLATFORM AND/OR INSTRUCTIONAL CONTENT; AND
					(B) IN NO EVENT SHALL ORKA'S CUMULATIVE AND AGGREGATE LIABILITY TO YOU
					UNDER OR RELATED TO THIS AGREEMENT EXCEED THREE HUNDRED DOLLARS
					($300). THE EXCLUSIONS AND LIMITATIONS IN THIS SECTION APPLY WHETHER
					THE ALLEGED LIABILITY IS BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT
					LIABILITY OR ANY OTHER BASIS, EVEN IF ORKA HAS BEEN ADVISED OF THE
					POSSIBILITY OF SUCH DAMAGE
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="18-your-responsibility-for-damages"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#18-your-responsibility-for-damages">
					18. Your Responsibility for Damages
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					To the extent an Instructional Course requires you to be present at an
					aircraft facility, operate an aircraft, or be a passenger on any
					aircraft, you agree as follows:
				</Typography>

				<div className="square-box spacing-content">
					<Typography className="normal-text" textAlign="justify">
						You assume and will assume all risks and responsibilities related to
						the operation of aircraft and your participation in related
						activities.
					</Typography>

					<Typography
						className="normal-text bold spacing-content"
						textAlign="justify">
						If damage to an aircraft or facility results from your actions or
						omissions, you agree to promptly pay to ORKA, as the Instructor's
						limited payment collection agent, (a) such amounts as are reasonably
						required to repair such damage; or (b) if less and in the
						Instructor's sole discretion, the insurance deductible required
						under the insurance policy applicable to such damage.
					</Typography>
				</div>
			</div>

			<div className="section spacing-section">
				<div id="19-indemnity" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#19-indemnity">
					19. Indemnity
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					You agree to indemnify, defend, protect, save and hold harmless ORKA
					from and against any and all claims, damages, losses, liabilities,
					judgments, awards, and costs (including reasonable attorneys fees)
					arising under or in connection with any Service Agreement you enter
					into.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="20-confidentiality" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#20-confidentiality">
					20. Confidentiality
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					The ORKA Platform, the Instructional Content, and all other content
					and materials provided by ORKA to you (collectively, "ORKA
					Confidential Information") are and shall remain the confidential and
					proprietary information of ORKA and its licensors. You must use and
					protect any ORKA Confidential Information in your possession or
					control against unauthorized use or disclosure. You may only use ORKA
					Confidential Information for the purposes for which it is provided by
					ORKA. ORKA will be entitled to seek injunctive and other equitable
					relief to enforce such obligations. These obligations of
					confidentiality shall survive expiration or termination of this
					Agreement.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="21-dispute-resolution" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#21-dispute-resolution">
					21. Dispute Resolution
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					If a dispute arises out of or relates to this Agreement, or the breach
					thereof, and if the dispute cannot be settled through negotiation, the
					parties agree first to try in good faith to settle the dispute by
					mediation administered by the American Arbitration Association in
					Savannah, Georgia, under its Commercial Mediation Procedures before
					resorting to arbitration, litigation, or some other dispute resolution
					procedure. If the parties are unable to settle such dispute through
					mediation, it shall be settled by arbitration administered by the
					American Arbitration Association in Savannah, Georgia, in accordance
					with its Commercial Arbitration Rules.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="22-miscellaneous" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#22-miscellaneous">
					22. Miscellaneous
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					This Agreement shall be governed by the laws of Georgia (without
					regard to principles of conflicts of law) as to all matters and the
					federal and state courts located in Chatham County, Georgia, shall be
					the exclusive venue for any suits or other legal proceedings arising
					under or related to this Agreement. This Agreement constitutes the
					complete and exclusive statement of the agreement between the parties
					and supersedes all proposals, oral or written, and all other
					communications between the parties relating to the subject matter of
					this Agreement. In the event any information posted on the ORKA
					Platform from time to time conflicts with any provision of this
					Agreement, the applicable provision of this Agreement shall control.
					This Agreement does not, and shall not be construed to, create any
					partnership, joint venture, employer- employee, agency or similar
					relationship between you and ORKA. You may not assign, transfer, or
					sublicense all or any of your rights or obligations under this
					Agreement without ORKA's express prior written consent. ORKA may
					assign, transfer, or sublicense all or any of ORKA's rights or
					obligations under this Agreement without restriction. The failure of
					ORKA to exercise or enforce any provision of this Agreement will not
					operate as a waiver of such provision. If any provision of this
					Agreement is held invalid or unenforceable, the remainder of this
					Agreement shall continue in full force and effect. You agree that a
					printed version of this Agreement and of any notice given in
					electronic form shall be admissible in judicial or administrative
					proceedings based upon or relating to this Agreement to the same
					extent and subject to the same conditions as other business documents
					and records originally generated and maintained in printed form. ORKA
					will not be responsible for failures to fulfill any obligations due to
					causes beyond its control.
				</Typography>
			</div>
		</Container>
	);
}
